// 合體效果處理器
// Merge Effect Handlers - 機械合體、吞食合體、寄生合體

import type { GameState, Card, BattlefieldPosition, BattlefieldSlot } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('MergeHandlers');

/**
 * 將卡牌加入玩家墓地 (immutable state update)
 */
function addToGraveyard(state: GameState, ownerId: string, card: Card): GameState {
  const playerKey = state.player1.id === ownerId ? 'player1' : 'player2';
  const graveyardCard: Card = { ...card, location: 'graveyard' as const, position: undefined, atkModifier: 0, scoreModifier: 0, keywords: undefined, mergedCards: undefined };
  return {
    ...state,
    [playerKey]: {
      ...state[playerKey],
      graveyard: [...state[playerKey].graveyard, graveyardCard],
    },
  };
}

/**
 * 將合體底下的卡牌送入墓地（不觸發離場效果 — 規則 7.10）
 * 也處理被吞食合體犧牲時，合體單位只有頂部卡牌參與合體（規則 7.12）
 */
function sendMergedCardsToGraveyard(state: GameState, card: Card, reason: string): { state: GameState; sentCards: Card[] } {
  const mergedCards = card.mergedCards ?? [];
  const sentCards: Card[] = [];
  let newState = state;
  for (const merged of mergedCards) {
    newState = addToGraveyard(newState, merged.ownerId, merged);
    sentCards.push(merged);
    log.debug(`底部卡牌 ${merged.id} 送入墓地 (${reason})`);
  }
  return { state: newState, sentCards };
}

/**
 * 找到戰場上的卡牌
 */
function findCardOnBattlefield(state: GameState, cardId: string): { card: Card; position: BattlefieldPosition } | null {
  const sides = [
    { side: state.battlefield.player1Side, _sideKey: 'player1' as const },
    { side: state.battlefield.player2Side, _sideKey: 'player2' as const },
  ];

  for (const { side } of sides) {
    for (const slot of [...side.frontLine, ...side.backLine]) {
      if (slot.unit?.id === cardId) {
        return { card: slot.unit, position: slot.position };
      }
    }
  }
  return null;
}

/**
 * 移除戰場上的卡牌
 */
function removeCardFromBattlefield(state: GameState, cardId: string): GameState {
  return {
    ...state,
    battlefield: {
      player1Side: {
        frontLine: state.battlefield.player1Side.frontLine.map(slot =>
          slot.unit?.id === cardId ? { ...slot, unit: null } : slot
        ),
        backLine: state.battlefield.player1Side.backLine.map(slot =>
          slot.unit?.id === cardId ? { ...slot, unit: null } : slot
        ),
      },
      player2Side: {
        frontLine: state.battlefield.player2Side.frontLine.map(slot =>
          slot.unit?.id === cardId ? { ...slot, unit: null } : slot
        ),
        backLine: state.battlefield.player2Side.backLine.map(slot =>
          slot.unit?.id === cardId ? { ...slot, unit: null } : slot
        ),
      },
    },
  };
}

/**
 * 更新戰場上的卡牌
 */
function updateCardOnBattlefield(state: GameState, cardId: string, updater: (card: Card) => Card): GameState {
  const mapSlot = (slot: BattlefieldSlot) =>
    slot.unit?.id === cardId ? { ...slot, unit: updater(slot.unit) } : slot;

  return {
    ...state,
    battlefield: {
      player1Side: {
        frontLine: state.battlefield.player1Side.frontLine.map(mapSlot),
        backLine: state.battlefield.player1Side.backLine.map(mapSlot),
      },
      player2Side: {
        frontLine: state.battlefield.player2Side.frontLine.map(mapSlot),
        backLine: state.battlefield.player2Side.backLine.map(mapSlot),
      },
    },
  };
}

/**
 * 機械合體處理器
 * 處理「移動到另一機械單位位置進行合體」
 * 合體後攻擊力/分數合併計算
 */
export class MechaMergeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('合體') && (text.includes('機械') || text.includes('移動到'))) ||
           text.includes('機械合體');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「移動到你場上另一機械單位位置進行合體」
    const mergeMatch = effectText.match(/移動到(.+?)位置(?:進行)?合體/);
    if (mergeMatch) {
      const isSelf = effectText.includes('你場上') || !effectText.includes('敵方');
      const targetType = effectText.includes('機械') ? 'robot' : undefined;

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'mechaMerge',
        requiresChoice: true,
        targetSelector: {
          zone: 'battlefield',
          side: isSelf ? 'self' : 'enemy',
          filter: targetType ? { faction: targetType } : undefined,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'mechaMerge') {
      return state;
    }

    const sourceId = action.sourceCardId;
    const targetId = action.targetCardId;
    if (!sourceId || !targetId) {
      log.debug('缺少來源或目標卡牌 ID');
      return state;
    }

    const sourceInfo = findCardOnBattlefield(state, sourceId);
    const targetInfo = findCardOnBattlefield(state, targetId);

    if (!sourceInfo || !targetInfo) {
      log.debug('找不到來源或目標卡牌');
      return state;
    }

    // 禁錮: bound source cannot initiate merge
    if (sourceInfo.card.keywords?.includes('bind')) {
      log.debug(`${sourceId} 禁錮中，無法發起合體`);
      return state;
    }

    // 從戰場移除來源卡牌
    let newState = removeCardFromBattlefield(state, sourceId);

    // Transfer keywords: Pierce, Poison, Ranged from source to target
    const transferKeywords = ['pierce', 'poison', 'ranged', 'block', 'taunt'];
    const sourceKeywords = sourceInfo.card.keywords ?? [];
    const targetKeywords = targetInfo.card.keywords ?? [];
    const transferred = transferKeywords.filter(kw => sourceKeywords.includes(kw) && !targetKeywords.includes(kw));
    const mergedKeywords = [...targetKeywords, ...transferred];

    // 將來源卡牌加入目標的 mergedCards
    newState = updateCardOnBattlefield(newState, targetId, (card) => ({
      ...card,
      mergedCards: [...(card.mergedCards || []), sourceInfo.card],
      // 合體後攻擊力疊加
      atkModifier: (card.atkModifier || 0) + (sourceInfo.card.atkModifier || 0),
      keywords: mergedKeywords.length > 0 ? mergedKeywords : card.keywords,
    }));

    // Source exits the battlefield via merge (rule 7.10)
    log.debug(`[unit_exit_merge] ${sourceId} 離場 (機械合體)`);
    // Merged unit "enters" as a new merged entity (rule 7.10)
    log.debug(`[unit_enter_merge] ${targetId} 合體完成，視為新的合體單位入場`);

    log.debug(`${sourceId} 合體到 ${targetId}`);

    return newState;
  }
}

/**
 * 吞食合體處理器
 * 處理「被獻祭時疊到打出單位下方」
 * 與祭品系統整合
 */
export class DevourMergeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('吞食') && text.includes('合體')) ||
           (text.includes('獻祭') && text.includes('疊到')) ||
           text.includes('吞噬');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「被獻祭時，疊到打出單位下方」
    if (effectText.includes('獻祭') && effectText.includes('疊到')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'devourMerge',
        // 此效果通常是被動觸發，不需要選擇
        requiresChoice: false,
      });
    }

    // 解析「吞食：選擇1個單位進行合體」
    const devourMatch = effectText.match(/吞食[：:](.+)/);
    if (devourMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'devourMerge',
        requiresChoice: true,
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'devourMerge') {
      return state;
    }

    const sourceId = action.sourceCardId;
    const targetId = action.targetCardId;
    if (!sourceId) {
      log.debug('缺少來源卡牌 ID');
      return state;
    }

    // 如果有目標，執行吞食合體
    if (targetId) {
      const sourceInfo = findCardOnBattlefield(state, sourceId);
      const targetInfo = findCardOnBattlefield(state, targetId);

      if (!sourceInfo || !targetInfo) {
        log.debug('找不到來源或目標卡牌');
        return state;
      }

      let newState = state;

      // Rule 7.12: If target is a merged unit being sacrificed for devour,
      // only the TOP card merges; bottom cards go to graveyard
      let targetCard = targetInfo.card;
      if (targetCard.mergedCards && targetCard.mergedCards.length > 0) {
        const splitResult = sendMergedCardsToGraveyard(newState, targetCard, 'devour_merge_split');
        newState = splitResult.state;
        // Update targetCard to reflect cleared mergedCards and reset modifiers
        targetCard = { ...targetCard, mergedCards: undefined, atkModifier: 0, scoreModifier: 0 };
        // Update the card on battlefield before removal
        newState = updateCardOnBattlefield(newState, targetId, () => targetCard);
        log.debug(`吞食合體：合體單位 ${targetId} 的底部卡牌已送入墓地，只有頂部卡牌參與合體`);
      }

      // Transfer keywords: Pierce, Poison, Ranged from devoured target to source
      const devourTransferKeywords = ['pierce', 'poison', 'ranged', 'block', 'taunt'];
      const devourTargetKws = targetCard.keywords ?? [];
      const devourSourceKws = sourceInfo.card.keywords ?? [];
      const devourTransferred = devourTransferKeywords.filter(kw => devourTargetKws.includes(kw) && !devourSourceKws.includes(kw));
      const devourMergedKws = [...devourSourceKws, ...devourTransferred];

      // 將目標卡牌加入來源的 mergedCards（吞食者獲得被吞食者）
      newState = removeCardFromBattlefield(newState, targetId);
      newState = updateCardOnBattlefield(newState, sourceId, (card) => ({
        ...card,
        mergedCards: [...(card.mergedCards || []), targetCard],
        keywords: devourMergedKws.length > 0 ? devourMergedKws : card.keywords,
      }));

      // Target exits the battlefield via devour merge (rule 7.10)
      log.debug(`[unit_exit_merge] ${targetId} 離場 (被吞食)`);
      // Merged source "enters" as a new merged entity (rule 7.10)
      // Note: 合體時 effects do NOT trigger for devour merge via sacrifice (rule 7.12)
      log.debug(`[unit_enter_merge] ${sourceId} 吞食合體完成 (合體時效果不觸發)`);

      log.debug(`${sourceId} 吞食了 ${targetId}`);
      return newState;
    }

    // 被動觸發情況（被獻祭時）- 由祭品系統呼叫
    log.debug(`${sourceId} 準備被吞食合體`);
    return state;
  }
}

/**
 * 寄生合體處理器
 * 處理「移動到目標單位下方進行合體」
 * 目標可能是敵方單位
 */
export class ParasiteMergeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('寄生') && text.includes('合體')) ||
           (text.includes('附著') && text.includes('合體')) ||
           (text.includes('移動到') && text.includes('下方'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「移動到敵方單位下方進行寄生合體」
    const parasiteMatch = effectText.match(/移動到(.+?)(?:單位)?下方/);
    if (parasiteMatch) {
      const isEnemy = effectText.includes('敵方') || effectText.includes('對手');
      const isSelf = effectText.includes('你場上');

      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'parasiteMerge',
        requiresChoice: true,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : isSelf ? 'self' : 'both',
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'parasiteMerge') {
      return state;
    }

    const sourceId = action.sourceCardId;
    const targetId = action.targetCardId;
    if (!sourceId || !targetId) {
      log.debug('缺少來源或目標卡牌 ID');
      return state;
    }

    const sourceInfo = findCardOnBattlefield(state, sourceId);
    const targetInfo = findCardOnBattlefield(state, targetId);

    if (!sourceInfo || !targetInfo) {
      log.debug('找不到來源或目標卡牌');
      return state;
    }

    // 禁錮: bound source cannot initiate parasite merge
    if (sourceInfo.card.keywords?.includes('bind')) {
      log.debug(`${sourceId} 禁錮中，無法發起寄生合體`);
      return state;
    }

    // 從戰場移除來源卡牌
    let newState = removeCardFromBattlefield(state, sourceId);

    // Transfer keywords: Pierce, Poison, Ranged from source to target
    const transferKeywords = ['pierce', 'poison', 'ranged', 'block', 'taunt'];
    const sourceKws = sourceInfo.card.keywords ?? [];
    const targetKws = targetInfo.card.keywords ?? [];
    const transferred = transferKeywords.filter(kw => sourceKws.includes(kw) && !targetKws.includes(kw));
    const mergedKeywords = [...targetKws, ...transferred];

    // 將來源卡牌加入目標的 mergedCards（寄生在目標下方）
    // 注意：寄生可能會改變目標的屬性或控制權
    newState = updateCardOnBattlefield(newState, targetId, (card) => ({
      ...card,
      mergedCards: [...(card.mergedCards || []), {
        ...sourceInfo.card,
        // 標記為寄生狀態
        activeEffects: [...(sourceInfo.card.activeEffects || []), 'parasiteAttached'],
      }],
      keywords: mergedKeywords.length > 0 ? mergedKeywords : card.keywords,
    }));

    // Source exits the battlefield via parasite merge (rule 7.10)
    log.debug(`[unit_exit_merge] ${sourceId} 離場 (寄生合體)`);
    // Merged target re-enters as a new merged entity (rule 7.10)
    log.debug(`[unit_enter_merge] ${targetId} 寄生合體完成，視為新的合體單位`);

    log.debug(`${sourceId} 寄生到 ${targetId}`);

    return newState;
  }
}

/**
 * 合體分離處理器
 * 處理「解除合體」效果
 * 分開合體單位，將合體的卡牌分別放到控制者場上作為獨立單位。
 * 如果原單位在前排，分離的單位優先放前排。
 * 附加的魔法卡送入墓地。如果空間不足，不執行分離。
 */
export class SeparateMergeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('解除合體') ||
           text.includes('分離') ||
           text.includes('脫離合體');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('解除合體') || effectText.includes('分離')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'separateMerge',
        requiresChoice: true,
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          // 只選擇有合體的單位 (在執行時檢查)
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'separateMerge') {
      return state;
    }

    const targetId = action.targetCardId || action.sourceCardId;
    if (!targetId) {
      log.debug('缺少目標卡牌 ID');
      return state;
    }

    const targetInfo = findCardOnBattlefield(state, targetId);
    if (!targetInfo || !targetInfo.card.mergedCards?.length) {
      log.debug('目標沒有合體的卡牌');
      return state;
    }

    const mergedCards = [...targetInfo.card.mergedCards];
    const mergedCount = mergedCards.length;

    // Find all empty slots on the controller's side
    const sideKey = targetInfo.position.side === 'player1' ? 'player1Side' : 'player2Side';
    const side = state.battlefield[sideKey];
    const emptySlots: BattlefieldPosition[] = [];

    const isFrontRow = targetInfo.position.row === 'front';

    // Collect empty slots in priority order
    const firstRow = isFrontRow ? 'front' : 'back';
    const secondRow = isFrontRow ? 'back' : 'front';
    const firstLine = isFrontRow ? side.frontLine : side.backLine;
    const secondLine = isFrontRow ? side.backLine : side.frontLine;

    for (const slot of firstLine) {
      if (!slot.unit) {
        emptySlots.push({ ...slot.position, row: firstRow });
      }
    }
    for (const slot of secondLine) {
      if (!slot.unit) {
        emptySlots.push({ ...slot.position, row: secondRow });
      }
    }

    // If not enough space, don't split
    if (emptySlots.length < mergedCount) {
      log.debug(`空間不足，無法分離 (需要 ${String(mergedCount)}，空位 ${String(emptySlots.length)})`);
      return state;
    }

    let newState = state;

    // Reset the original card: remove merged cards and merge bonuses
    newState = updateCardOnBattlefield(newState, targetId, (card) => ({
      ...card,
      mergedCards: undefined,
      atkModifier: 0,
      scoreModifier: 0,
    }));

    // Place each merged card as a new unit on the field
    const ownerKey = targetInfo.position.side === 'player1' ? 'player1' : 'player2';
    for (let i = 0; i < mergedCount; i++) {
      const merged = mergedCards[i];
      const pos = emptySlots[i];

      const newUnit: Card = {
        ...merged,
        id: `${merged.id}_sep_${String(Date.now())}_${String(i)}`,
        ownerId: newState[ownerKey].id,
        location: 'battlefield' as const,
        position: pos,
        orientation: 'upright' as const,
        summonedThisTurn: true, // counts as entering
        mergedCards: undefined,
        atkModifier: 0,
        scoreModifier: 0,
        activeEffects: [],
      };

      // Place on battlefield
      const lineKey = pos.row === 'front' ? 'frontLine' : 'backLine';
      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newLine = [...newSide[lineKey]];
      newLine[pos.column - 1] = {
        ...newLine[pos.column - 1],
        unit: newUnit,
      };
      newSide[lineKey] = newLine;
      newBattlefield[sideKey] = newSide;
      newState = { ...newState, battlefield: newBattlefield };
    }

    log.debug(`從 ${targetId} 分離了 ${String(mergedCount)} 張合體卡牌到場上`);

    return newState;
  }
}
